import { UseQueryResult } from '@edreamsodigeo/frontend-api-client';
import {
  SearchItineraryQuery,
  SearchItineraryQueryVariables,
} from 'generated/graphql';
import type { SearchQueryResult } from '../types';
import {
  arrayToMap,
  getPreferredCommercialCarrierId,
} from './transformItinerarySearchResponse';

type SearchItineraryResponse = UseQueryResult<
  SearchItineraryQuery,
  SearchItineraryQueryVariables
>;

export const transformSearchItineraryResponse = ({
  data,
}: {
  data: SearchItineraryResponse['data'];
}): SearchQueryResult['data'] => {
  const { searchItinerary } = data || {};
  if (!searchItinerary) return null;

  const {
    insuranceOffers,
    carbonFootprints,
    campaignConfigs,
    locations,
    carriers,
    priceTypeDisplayed,
    sections,
    segments,
    ...rest
  } = searchItinerary;
  // Turn arrays into id maps so we can easily access them by id instead
  // of iterating through the array every time we need a value
  const itinerarySearchMap = {
    ...searchItinerary,
    locations: arrayToMap(locations, 'location'),
    carriers: arrayToMap(carriers, 'carrier'),
    insuranceOffers: arrayToMap(insuranceOffers, 'insuranceOffer'),
    carbonFootprints: arrayToMap(carbonFootprints, 'carbonFootprint'),
    campaignConfigs: arrayToMap(campaignConfigs, 'campaignConfig'),
    sections: arrayToMap(sections, 'section'),
    segments: arrayToMap(segments, 'segment'),
  };

  return {
    search: {
      ...rest,
      __typename: 'SearchResponse',
      defaultFeeType: { id: priceTypeDisplayed as string },
      itineraries: searchItinerary?.itineraries.map((itinerary) => {
        const { campaignConfigId, carbonFootprintId, ...rest } = itinerary;

        return {
          ...rest,
          id: itinerary.key,
          __typename: 'Itinerary',
          fees: rest.fees.map((fee) => ({
            price: fee.price,
            type: { id: fee.type },
          })),
          campaignConfig:
            itinerarySearchMap.campaignConfigs[campaignConfigId as string],
          carbonFootprint:
            itinerarySearchMap.carbonFootprints[carbonFootprintId as string],
          legs: itinerary.legs?.map((leg) => {
            const segment = itinerarySearchMap.segments[leg.segmentId];
            let commercialBrandSegment;
            return {
              ...leg,
              __typename: 'Leg',
              segmentKeys: [0],
              segments: [
                {
                  ...segment,
                  __typename: 'Segment',
                  carrier:
                    itinerarySearchMap.carriers[
                      getPreferredCommercialCarrierId(
                        segment.carrierId,
                        commercialBrandSegment
                      )
                    ],
                  sections: segment.sections.map((sectionId) => {
                    const section = itinerarySearchMap.sections[sectionId];
                    const {
                      departureId,
                      destinationId,
                      carrierId,
                      operatingCarrierId,
                      commercialBrandId,
                      insuranceOfferId,
                      ...rest
                    } = section;
                    commercialBrandSegment = commercialBrandId;
                    return {
                      ...rest,
                      __typename: 'Section',
                      insuranceOffer:
                        insuranceOfferId != null
                          ? itinerarySearchMap.insuranceOffers[insuranceOfferId]
                          : null,
                      departure: itinerarySearchMap.locations[departureId],
                      destination: itinerarySearchMap.locations[destinationId],
                      carrier:
                        itinerarySearchMap.carriers[
                          getPreferredCommercialCarrierId(
                            carrierId,
                            commercialBrandId
                          )
                        ],
                      operatingCarrier:
                        itinerarySearchMap.carriers[
                          operatingCarrierId as string
                        ],
                      commercialBrand:
                        itinerarySearchMap.carriers[
                          commercialBrandId as string
                        ],
                    };
                  }),
                },
              ],
            };
          }),
        };
      }),
    },
  };
};
